import React from "react";
import { Crown, Gem } from "lucide-react";
import { useStripe } from "../contexts/stripeProvider";

/**
 * SubscriptionBadge Component
 *
 * Exibe um badge visual indicando o nível de assinatura do usuário
 * Premium = Coroa dourada
 * Diamond = Diamante azul
 *
 * @param {Object} props
 * @param {string} props.userId - ID do usuário (opcional, usa contexto se não fornecido)
 * @param {string} props.planId - ID do plano ('premium', 'diamond', 'free')
 * @param {'small'|'medium'|'large'} props.size - Tamanho do badge
 * @param {'icon'|'badge'|'tag'} props.variant - Variante visual do badge
 * @param {string} props.className - Classes CSS adicionais
 * @param {boolean} props.showLabel - Se deve mostrar o label junto com o ícone
 */
const SubscriptionBadge = ({
  userId = null,
  planId = null,
  size = "medium",
  variant = "icon",
  className = "",
  showLabel = false,
}) => {
  const { subscriptionStatus } = useStripe();

  // Se não foi passado planId, usa o contexto do Stripe
  let tier = planId;
  if (!tier && !userId) {
    if (subscriptionStatus?.isDiamond) {
      tier = "diamond";
    } else if (subscriptionStatus?.isPremium) {
      tier = "premium";
    } else {
      tier = "free";
    }
  }

  // Não mostra badge para usuários free
  if (!tier || tier === "free") {
    return null;
  }

  // Configuração de tamanhos
  const sizeClasses = {
    small: {
      icon: "w-3 h-3",
      badge: "w-4 h-4 p-0.5",
      tag: "px-1.5 py-0.5 text-[10px]",
      iconSize: 10,
    },
    medium: {
      icon: "w-4 h-4",
      badge: "w-5 h-5 p-1",
      tag: "px-2 py-0.5 text-xs",
      iconSize: 14,
    },
    large: {
      icon: "w-5 h-5",
      badge: "w-6 h-6 p-1",
      tag: "px-3 py-1 text-sm",
      iconSize: 18,
    },
  };

  // Configuração por tier
  const tierConfig = {
    premium: {
      icon: Crown,
      color: "text-yellow-500",
      bgColor: "bg-gradient-to-br from-yellow-400 to-yellow-600",
      label: "Premium",
      tagBg: "bg-gradient-to-r from-yellow-500 to-yellow-600",
    },
    diamond: {
      icon: Gem,
      color: "text-blue-500",
      bgColor: "bg-gradient-to-br from-blue-400 to-purple-600",
      label: "Diamond",
      tagBg: "bg-gradient-to-r from-blue-500 to-purple-600",
    },
  };

  const config = tierConfig[tier];
  if (!config) return null;

  const Icon = config.icon;
  const sizeConfig = sizeClasses[size];

  // Variant: Apenas ícone
  if (variant === "icon") {
    return (
      <Icon
        className={`${config.color} ${sizeConfig.icon} ${className}`}
        size={sizeConfig.iconSize}
        fill="currentColor"
      />
    );
  }

  // Variant: Badge circular com fundo
  if (variant === "badge") {
    return (
      <div
        className={`${config.bgColor} ${sizeConfig.badge} rounded-full flex items-center justify-center shadow-sm ${className}`}
        title={config.label}
      >
        <Icon className="text-white" size={sizeConfig.iconSize} fill="white" />
      </div>
    );
  }

  // Variant: Tag com label
  if (variant === "tag") {
    return (
      <div
        className={`inline-flex items-center gap-1 ${config.tagBg} ${sizeConfig.tag} rounded-full text-white font-semibold shadow-sm ${className}`}
      >
        <Icon className="text-white" size={sizeConfig.iconSize} fill="white" />
        {showLabel && <span>{config.label}</span>}
      </div>
    );
  }

  return null;
};

/**
 * AvatarWithBadge Component
 *
 * Componente helper que renderiza um avatar com badge de assinatura sobreposto
 * e anel colorido no círculo baseado no plano ativo.
 *
 * @param {Object} props
 * @param {string} props.src - URL da imagem do avatar
 * @param {string} props.alt - Texto alternativo
 * @param {string} props.planId - ID do plano ('premium', 'diamond', 'free')
 * @param {string} props.planStatus - Status do plano ('active', 'inactive', 'canceled', 'past_due')
 * @param {'small'|'medium'|'large'|'xlarge'} props.size - Tamanho do avatar
 * @param {string} props.className - Classes CSS adicionais
 * @param {'top-right'|'bottom-right'|'top-left'|'bottom-left'} props.badgePosition - Posição do badge
 */
export const AvatarWithBadge = ({
  src,
  alt,
  planId,
  planStatus = null,
  size = "medium",
  className = "",
  badgePosition = "bottom-right",
  ...props
}) => {
  const sizeClasses = {
    small: { avatar: "w-8 h-8", badge: "small" },
    medium: { avatar: "w-12 h-12", badge: "small" },
    large: { avatar: "w-16 h-16", badge: "medium" },
    xlarge: { avatar: "w-24 h-24", badge: "medium" },
  };

  const positionClasses = {
    "top-right": "-top-0.5 -right-0.5",
    "bottom-right": "-bottom-0.5 -right-0.5",
    "top-left": "-top-0.5 -left-0.5",
    "bottom-left": "-bottom-0.5 -left-0.5",
  };

  const ringConfig = {
    premium: "bg-gradient-to-br from-yellow-400 to-yellow-600",
    diamond: "bg-gradient-to-br from-blue-400 to-purple-600",
  };

  const config = sizeClasses[size];

  // Plano expirado quando planStatus foi fornecido e não é 'active'
  const isPlanActive = !planStatus || planStatus === "active";
  const showRing =
    planId && planId !== "free" && isPlanActive && ringConfig[planId];

  const FALLBACK_SRC = "/assets/images/no_image.png";
  const handleImgError = (e) => {
    if (e.target.src.endsWith(FALLBACK_SRC)) return;
    e.target.src = FALLBACK_SRC;
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {showRing ? (
        <div className={`${ringConfig[planId]} p-[2px] rounded-full`}>
          <img
            src={src || FALLBACK_SRC}
            alt={alt}
            onError={handleImgError}
            className={`${config.avatar} rounded-full object-cover block bg-muted overflow-hidden`}
            {...props}
          />
        </div>
      ) : (
        <img
          src={src || FALLBACK_SRC}
          alt={alt}
          onError={handleImgError}
          className={`${config.avatar} rounded-full object-cover block bg-muted overflow-hidden`}
          {...props}
        />
      )}
      {planId && planId !== "free" && (
        <div className={`absolute ${positionClasses[badgePosition]} z-10`}>
          <SubscriptionBadge
            planId={planId}
            size={config.badge}
            variant="badge"
          />
        </div>
      )}
    </div>
  );
};

/**
 * NameWithBadge Component
 *
 * Componente helper que renderiza um nome com badge inline
 *
 * @param {Object} props
 * @param {string} props.name - Nome do usuário
 * @param {string} props.planId - ID do plano ('premium', 'diamond', 'free')
 * @param {'small'|'medium'|'large'} props.size - Tamanho do badge
 * @param {string} props.className - Classes CSS adicionais para o container
 * @param {string} props.nameClassName - Classes CSS adicionais para o nome
 * @param {boolean} props.showLabel - Se deve mostrar o label no badge
 */
export const NameWithBadge = ({
  name,
  planId,
  size = "medium",
  className = "",
  nameClassName = "",
  showLabel = false,
}) => {
  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <span className={nameClassName}>{name}</span>
      {planId && planId !== "free" && (
        <SubscriptionBadge
          planId={planId}
          size={size}
          variant="icon"
          showLabel={showLabel}
        />
      )}
    </div>
  );
};

export default SubscriptionBadge;
